import { useMutation } from '@tanstack/react-query';
import type { QuoteItemDTO } from '@setvi/shared/services/react-query';
import { refreshQuoteV2ItemPriceMutation } from 'Services/Mutations/QuotesV2';
import {
  mapQuoteItems,
  getItemId,
  updateItemFromServerResponse
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface RefreshSinglePriceParams {
  productId: string;
  sku: string;
}

/**
 * Mark a specific product as needing pricing update
 */
const markProductForPricing = (
  items: QuoteItemDTO[],
  productId: string
): QuoteItemDTO[] =>
  mapQuoteItems(items, product => {
    if (getItemId(product) === productId && !product.isFreeHand) {
      return { ...product, isPricingFetching: true };
    }
    return product;
  });

/**
 * Refresh price for a single product via the V2 per-item endpoint.
 * The server fetches the latest price internally.
 */
export const useRefreshSinglePrice = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = refreshQuoteV2ItemPriceMutation();

  return useMutation({
    mutationFn: ({ productId }: RefreshSinglePriceParams) =>
      mutationFn({ quoteId, itemId: productId }),

    onMutate: async ({ productId }) => {
      await cache.cancel();

      const previousItems = cache.getItems();

      // Mark specific product as fetching for loading state
      cache.setItems(old => {
        if (!old) return old;
        return markProductForPricing(old, productId);
      });

      return { previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: (data: any) => {
      // Apply new price from server response
      if (data?.item) {
        cache.setItems(old =>
          updateItemFromServerResponse(old || [], data.item, p => ({
            sell: p.sell,
            cost: p.cost,
            totalGrossProfit: p.marginAmount,
            totalGrossProfitPercentage: p.margin,
            isPricingFetching: false
          }))
        );
      } else {
        // Fallback: clear loading state
        cache.setItems(old => {
          if (!old) return old;
          return mapQuoteItems(old, product => {
            if (product.isPricingFetching) {
              return { ...product, isPricingFetching: false };
            }
            return product;
          });
        });
      }

      // Only refetch quote (for totals), NOT items — refetching items
      // would overwrite enriched data with lightweight metadata.
      cache.refetchQuote();
    }
  });
};
